import { useMutation } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '@/lib/api/errors';
import { Button, Input } from '@/ui';
import { forgotPassword } from './api';
import type { ForgotPasswordResult } from './api';
import { AuthShell } from './AuthShell';
import styles from './AuthForm.module.css';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');

  const mutation = useMutation<ForgotPasswordResult, unknown, string>({
    mutationFn: (value: string) => forgotPassword(value),
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    mutation.mutate(email);
  };

  const result = mutation.data;

  /**
   * The same confirmation regardless of whether the account exists.
   *
   * With email enabled the backend already does this, but with email disabled it
   * returns `not_found` for a miss — which would turn this screen into an account
   * enumeration oracle. We do not surface that distinction.
   */
  const confirmation =
    result !== undefined
      ? 'If an account matches that, a reset link has been sent. The link is valid for 30 minutes.'
      : null;

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your username, email, or official email and we'll send a reset link."
      footer={
        <Link className={styles.link} to="/login">
          Back to sign in
        </Link>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.formError} role="alert">
            {errorMessage(mutation.error)}
          </p>
        )}

        {confirmation !== null && (
          <div className={styles.success} role="status">
            {confirmation}
            {/*
              With PASSWORD_RESET_EMAIL_ENABLED=false the backend returns the reset URL
              in the response body. That is a development convenience and must never be
              rendered in a production build.
            */}
            {import.meta.env.DEV && result?.status === 'ok' && result.resetUrl !== null && (
              <p className={styles.devToken}>Dev only — reset link: {result.resetUrl}</p>
            )}
          </div>
        )}

        <Input
          label="Username or email"
          icon={Mail}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="username"
          required
          disabled={mutation.isPending}
        />

        <Button
          type="submit"
          fullWidth
          size="lg"
          loading={mutation.isPending}
          disabled={email.trim() === ''}
        >
          Send reset link
        </Button>
      </form>
    </AuthShell>
  );
}
